import { useState, useCallback } from "react";
import { ApiService, ApiError } from "../services/api";
import { appKitBlockchainService as blockchainService } from "../services/appkitBlockchain";
import { soundService } from "../services/sound";
import { Game, GameState, GameStatus, GameUtilities } from "../types";
import { sleep } from "../services/utils";

export const useGame = () => {
  const [gameState, setGameState] = useState<GameState>({
    loading: false,
    wallet: { connected: false },
  });

  // Helper function to extract user-friendly error message
  const getErrorMessage = (error: unknown, fallbackMessage: string): string => {
    if (error instanceof ApiError) {
      // Use the detailed error message from the backend
      return error.message;
    } else if (error instanceof Error) {
      return error.message;
    }
    return fallbackMessage;
  };

  const createGame = useCallback(
    async (
      betAmount: string,
      walletAddress: string,
      utilities?: GameUtilities,
    ): Promise<Game> => {
      setGameState((prev) => ({ ...prev, loading: true, error: undefined }));

      try {
        // Transform utilities from GameUtilities format to API format
        const apiUtilities = utilities ? {
          rowSkip: utilities.rowSkip?.purchased || false,
          dxfMiningBoost: utilities.dxfMiningBoost?.purchased ? utilities.dxfMiningBoost.count : 0,
          phoenixRevival: utilities.phoenixRevival?.purchased || false,
        } : undefined;

        if (utilities?.phoenixRevival?.purchased) {
          soundService.loadPhoenixSound();
        }

        // Create game via backend API
        const createResponse = await ApiService.createGame({
          playerAddress: walletAddress,
          betAmount,
          utilities: apiUtilities,
        });

        // Create game on blockchain
        const txHash = await blockchainService.createGameOnChain(
          createResponse.gameId,
          createResponse.gameSeedHash,
          createResponse.algoVersion,
          createResponse.gameConfig,
          createResponse.deadline,
          createResponse.signature,
          betAmount,
          createResponse.dxfUtilityAmount || "0",
        );

        console.log("Game created on blockchain:", txHash);

        let game: Game | undefined = undefined;
        let interval = 200;
        while (game?.status !== "active") {
          game = await ApiService.getGame(createResponse.gameId);
          await sleep(interval);
          interval = interval * 2;
        }

        setGameState((prev) => ({
          ...prev,
          game,
          loading: false,
        }));

        return game;
      } catch (error) {
        console.error("Error creating game:", error);

        setGameState((prev) => ({
          ...prev,
          loading: false,
          error: getErrorMessage(error, "Failed to create game"),
        }));
        throw error;
      }
    },
    [],
  );

  const makeMove = useCallback(
    async (gameId: string, tileIndex: number) => {
      setGameState((prev) => ({ ...prev, loading: true, error: undefined }));

      try {
        const moveResponse = await ApiService.makeMove(gameId, { tileIndex });

        // Play sound based on move result
        const lastMove = moveResponse.moves[moveResponse.moves.length - 1];
        if (lastMove) {
          if (lastMove.result === "death") {
            soundService.playDeathTile();
          } else if (lastMove.result === "safe") {
            const deathTile = moveResponse.deathTiles?.find((tile) => tile.row === lastMove.row);
            if (deathTile && Math.abs(lastMove.tileIndex - deathTile?.position) <= 1) {
              // nearly die
              soundService.playDangerTile();
            } else {
              soundService.playGoodTile();
            }
          } else if (lastMove.result === "revived") {
            soundService.playPhoenixSound()
          }
        }

        // Update game state
        const updatedGame: Game = {
          gameId: gameId,
          gameSeedHash: gameState.game?.gameSeedHash || "",
          rowConfigs: gameState.game?.rowConfigs || [],
          status: moveResponse.status as GameStatus,
          currentRow: moveResponse.currentRow,
          moves: moveResponse.moves,
          utilities: moveResponse.utilities,
          seed: moveResponse.gameSeed,
          deathTiles: moveResponse.deathTiles,
          playerAddress: gameState.game?.playerAddress || "",
          betAmount: gameState.game?.betAmount || "0",
        };

        setGameState((prev) => ({
          ...prev,
          game: updatedGame,
          loading: false,
        }));

        return moveResponse;
      } catch (error) {
        console.error("Error making move:", error);
        setGameState((prev) => ({
          ...prev,
          loading: false,
          error: getErrorMessage(error, "Failed to make move"),
        }));
        throw error;
      }
    },
    [gameState.game],
  );

  const skipRow = useCallback(
    async (gameId: string) => {
      setGameState((prev) => ({ ...prev, loading: true, error: undefined }));

      try {
        const moveResponse = await ApiService.skipRow(gameId);

        // Play a unique sound for row skip
        soundService.playGoodTile(); // We could add a unique skip sound later

        // Update game state
        const updatedGame: Game = {
          gameId: gameId,
          gameSeedHash: gameState.game?.gameSeedHash || "",
          rowConfigs: gameState.game?.rowConfigs || [],
          status: moveResponse.status as GameStatus,
          currentRow: moveResponse.currentRow,
          moves: moveResponse.moves,
          utilities: moveResponse.utilities,
          seed: moveResponse.gameSeed,
          deathTiles: moveResponse.deathTiles,
          playerAddress: gameState.game?.playerAddress || "",
          betAmount: gameState.game?.betAmount || "0",
        };

        setGameState((prev) => ({
          ...prev,
          game: updatedGame,
          loading: false,
        }));

        return moveResponse;
      } catch (error) {
        console.error("Error skipping row:", error);
        setGameState((prev) => ({
          ...prev,
          loading: false,
          error: getErrorMessage(error, "Failed to skip row"),
        }));
        throw error;
      }
    },
    [gameState.game],
  );

  const cashOut = useCallback(async (gameId: string, createGameAgain?: boolean) => {
    setGameState((prev) => ({ ...prev, loading: true, error: undefined }));

    try {
      // Get cash out signature from backend (with optional new game creation)
      const cashOutData = await ApiService.getCashOutSignature(gameId, createGameAgain);
      
      // Update game state
      setGameState((prev) => ({
        ...prev,
        game: {
          ...prev.game!,
          seed: cashOutData.gameSeed,
          status: "cashing_out",
        },
        loading: true,
      }));

      // Execute cash out on blockchain (or cash out + create game for reinvestment)
      let txHash: string;
      if (createGameAgain && cashOutData.newGame) {
        // Use cashOutAndCreateGame for atomic operation
        txHash = await blockchainService.cashOutAndCreateGameOnChain(
          cashOutData, // Cash out signature data
          {
            // New game parameters as struct
            gameId: cashOutData.newGame.gameId,
            gameSeedHash: cashOutData.newGame.gameSeedHash,
            algoVersion: cashOutData.newGame.algoVersion,
            gameConfig: cashOutData.newGame.gameConfig,
            deadline: cashOutData.newGame.deadline,
            signature: cashOutData.newGame.signature,
            betAmount: gameState.game?.betAmount || "0",
            dxfUtilityAmount: cashOutData.newGame.dxfUtilityAmount || "0",
          }
        );
      } else {
        // Regular cash out
        txHash = await blockchainService.cashOutOnChain(cashOutData);
      }
      
      console.log("Cash out transaction:", txHash);

      // If reinvestment, poll for new game activation
      if (createGameAgain && cashOutData.newGame) {
        let newGame: Game | undefined = undefined;
        let interval = 200;
        
        // Poll until the new game becomes active
        while (newGame?.status !== "active") {
          newGame = await ApiService.getGame(cashOutData.newGame.gameId);
          await sleep(interval);
          interval = interval * 2;
        }
        
        // Update game state with new game
        setGameState((prev) => ({
          ...prev,
          game: newGame,
          loading: false,
        }));
        
        return { 
          txHash, 
          payoutAmount: cashOutData.payoutAmount,
          newGame,
        };
      } else {
        // Regular cash out flow
        await ApiService.markCashedOut(gameId);

        // Update game state
        setGameState((prev) => ({
          ...prev,
          game: {
            ...prev.game!,
            status: "cashed_out",
          },
          loading: false,
        }));

        return { txHash, payoutAmount: cashOutData.payoutAmount };
      }
    } catch (error) {
      console.error("Error cashing out:", error);
      setGameState((prev) => ({
        ...prev,
        loading: false,
        error: getErrorMessage(error, "Failed to cash out"),
      }));
      throw error;
    }
  }, [gameState.game?.betAmount]);

  const getGame = useCallback(async (gameId: string): Promise<Game> => {
    setGameState((prev) => ({ ...prev, loading: true, error: undefined }));

    try {
      const game = await ApiService.getGame(gameId);

      setGameState((prev) => ({
        ...prev,
        game: {
          ...game,
          status: game.status === "pending" ? "active" : game.status,
        },
        loading: false,
      }));

      return game;
    } catch (error) {
      console.error("Error getting game:", error);
      setGameState((prev) => ({
        ...prev,
        loading: false,
        error: getErrorMessage(error, "Failed to get game"),
      }));
      throw error;
    }
  }, []);

  const verifyGame = useCallback(
    async (gameId: string) => {
      if (!gameState.game?.seed) {
        throw new Error("Game seed not available for verification");
      }

      try {
        const result = await ApiService.verifyGame(
          gameId,
          gameState.game.seed,
          gameState.game.rowConfigs,
          gameState.game.gameSeedHash,
        );
        return result;
      } catch (error) {
        console.error("Error verifying game:", error);
        throw error;
      }
    },
    [gameState.game],
  );

  const resetGame = useCallback(() => {
    setGameState((prev) => ({
      ...prev,
      game: undefined,
      error: undefined,
    }));
  }, []);

  const checkForActiveGame = useCallback(async () => {
    setGameState((prev) => ({ ...prev, loading: true, error: undefined }));

    try {
      const { activeGame, hasActiveGame } = await ApiService.getActiveGame();

      if (hasActiveGame && activeGame) {
        // check on chain status
        const gameDetails = await blockchainService.getGameDetails(
          activeGame.gameId,
        );
        if (activeGame.status === "cashing_out" && gameDetails.status > 0) {
          activeGame.status = "cashed_out";
          ApiService.markCashedOut(activeGame.gameId);
        }
        setGameState((prev) => ({
          ...prev,
          game: activeGame,
          loading: false,
        }));
        return activeGame;
      } else {
        setGameState((prev) => ({
          ...prev,
          game: undefined,
          loading: false,
        }));
        return null;
      }
    } catch (error) {
      console.error("Error checking for active game:", error);
      setGameState((prev) => ({
        ...prev,
        loading: false,
        error: getErrorMessage(error, "Failed to check for active game"),
      }));
      return null;
    }
  }, []);

  return {
    gameState,
    createGame,
    makeMove,
    skipRow,
    cashOut,
    getGame,
    verifyGame,
    resetGame,
    checkForActiveGame,
  };
};
